import { ArrowUpDown, Settings2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { useDiscoverStore } from '../stores/discover-store'
import { TAG_GROUPS } from '../types/tags'
import { TagsSelector } from './tags-selector'

interface TagsProps {
  className?: string
}

export function Tags({ className }: TagsProps) {
  const { tags, sort } = useDiscoverStore((s) => s.searchParams)
  const { isOpen } = useDiscoverStore((s) => s.tagsSelector)
  const {
    handleToggleTag,
    handleExclusiveSelectTag,
    handleClearTags,
    handleSetTagsSelectorOpen,
    handleToggleOrder,
  } = useDiscoverStore((s) => s.handlers)

  const getSelectedValue = (groupKey: 'identity' | 'orientation') => {
    const group = TAG_GROUPS[groupKey]
    return tags.find((t) => (group.tags as string[]).includes(t)) || 'all'
  }

  return (
    <div
      className={cn(
        'no-scrollbar flex flex-1 items-center gap-2 overflow-x-auto',
        className
      )}
    >
      {/* 主标签与互斥选项 */}
      <div className='flex shrink-0 items-center gap-2'>
        <Badge
          variant={tags.length === 0 ? 'default' : 'secondary'}
          className={cn(
            'h-8 cursor-pointer rounded-full px-4 text-xs font-medium transition-all active:scale-95',
            tags.length === 0
              ? 'bg-primary hover:bg-primary/95 text-primary-foreground shadow-sm'
              : 'bg-muted/60 hover:bg-muted text-muted-foreground border-transparent font-normal'
          )}
          onClick={handleClearTags}
        >
          全部
        </Badge>

        {/* 排序方向切换按钮 */}
        <Badge
          variant={sort === 'updated_at' ? 'default' : 'secondary'}
          className={cn(
            'h-8 cursor-pointer rounded-full px-3 text-xs font-medium transition-all active:scale-95',
            sort === 'updated_at'
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'bg-muted/40 hover:bg-muted/60 text-muted-foreground border-transparent',
            'flex items-center gap-1.5'
          )}
          onClick={handleToggleOrder}
        >
          <ArrowUpDown className='size-3.5' />
          {sort === 'updated_at' ? '最近更新' : '热度排序'}
        </Badge>

        <Select
          value={getSelectedValue('identity')}
          onValueChange={(val) => handleExclusiveSelectTag('identity', val)}
        >
          <SelectTrigger className='bg-muted/40 hover:bg-muted/60 h-8 w-fit min-w-[100px] rounded-full border-none px-3 text-xs font-medium focus-visible:outline-none'>
            <span className='mr-1 opacity-70'>玩家身份:</span>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>全部</SelectItem>
            {TAG_GROUPS.identity.tags.map((tag) => (
              <SelectItem key={tag} value={tag}>
                {tag}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={getSelectedValue('orientation')}
          onValueChange={(val) => handleExclusiveSelectTag('orientation', val)}
        >
          <SelectTrigger className='bg-muted/40 hover:bg-muted/60 h-8 w-fit min-w-[100px] rounded-full border-none px-3 text-xs font-medium focus-visible:outline-none'>
            <span className='mr-1 opacity-70'>类型:</span>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>全部</SelectItem>
            {TAG_GROUPS.orientation.tags.map((tag) => (
              <SelectItem key={tag} value={tag}>
                {tag}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* 次标签选择器 Dialog */}
        <Dialog open={isOpen} onOpenChange={handleSetTagsSelectorOpen}>
          <DialogTrigger asChild>
            <Button
              variant='secondary'
              size='sm'
              className='bg-muted/40 hover:bg-muted/60 h-8 gap-1.5 rounded-full border-none px-3 text-xs font-medium'
            >
              <Settings2 className='size-3.5' />
              标签筛选
            </Button>
          </DialogTrigger>
          <DialogContent className='sm:max-w-[480px]'>
            <DialogHeader>
              <DialogTitle>标签筛选</DialogTitle>
            </DialogHeader>
            <TagsSelector />
          </DialogContent>
        </Dialog>
      </div>

      {/* 竖向分割线 */}
      <Separator
        orientation='vertical'
        className='mx-0.5 h-5 shrink-0 opacity-40'
      />

      {/* 次标签（题材）多选 */}
      <div className='flex items-center gap-1.5'>
        {TAG_GROUPS.genre.tags.map((tag) => {
          const isSelected = tags.includes(tag)
          return (
            <Badge
              key={tag}
              variant={isSelected ? 'default' : 'secondary'}
              className={cn(
                'h-8 shrink-0 cursor-pointer rounded-full px-3 text-xs font-normal transition-all active:scale-95',
                isSelected
                  ? 'bg-accent text-accent-foreground hover:bg-accent/90 border-transparent shadow-xs'
                  : 'bg-muted/30 hover:bg-muted/50 text-muted-foreground border-transparent'
              )}
              onClick={() => handleToggleTag(tag)}
            >
              {tag}
            </Badge>
          )
        })}
      </div>
    </div>
  )
}
